import {useEffect, useState} from "react";
import {Box, CircularProgress, Grid, Switch} from "@mui/material";
import _ from "lodash";
import {msgSource as msgSourceConstants} from "_constants";
import {organizationSettingsService} from "_services";
import {SnackBar} from "components/common";
import {Auth} from "components/Authorization";
import BasicButton from "../../../../common/BasicButton";
import commonStyles from "../commonStyles";
import GorgiasViewsSetupEntry from "./GorgiasViewsSetup/GorgiasViewsSetupEntry";

function handleApiError(error, fallback) {
    if (_.get(error, 'response.status') === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, 'response.data.message') || _.get(error, 'message') || fallback;
    SnackBar.showMessage(message);
}

function useToggle({initial, persist, successMessage, fallbackMessage}) {
    const [value, setValue] = useState(Boolean(initial));
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setValue(Boolean(initial));
    }, [initial]);

    const onChange = async (event) => {
        if (submitting) return;
        const previous = value;
        const next = event.target.checked;
        setSubmitting(true);
        setValue(next);
        try {
            const updated = await persist(next);
            const persistedValue = typeof updated === 'boolean' ? updated : next;
            setValue(persistedValue);
            SnackBar.showMessage(successMessage(persistedValue));
        } catch (error) {
            setValue(previous);
            handleApiError(error, fallbackMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return {value, submitting, onChange};
}

function ToggleField({label, toggle}) {
    return (
        <>
            <Box sx={commonStyles.labelMedium}>{label}</Box>
            <Box sx={styles.toggleRow}>
                <Switch
                    checked={toggle.value}
                    disabled={toggle.submitting}
                    onChange={toggle.onChange}
                />
                {toggle.submitting && <CircularProgress size={16} sx={styles.spinner} />}
            </Box>
        </>
    );
}

function ActionField({label, buttonLabel, busy, disabled, onAction}) {
    return (
        <>
            <Box sx={commonStyles.labelMedium}>{label}</Box>
            <Box sx={styles.actionRow}>
                <Box sx={styles.actionButton}>
                    <BasicButton
                        label={buttonLabel}
                        action={onAction}
                        disabled={disabled || busy}
                        endIcon={busy ? <CircularProgress size={16} sx={styles.spinner} /> : null}
                    />
                </Box>
            </Box>
        </>
    );
}

export default function MsgDataSourceTrendsToggles({msgSourceName, trendsMsgDataSource, label}) {
    const trendsId = trendsMsgDataSource?.id;
    const isFacebook = msgSourceName === msgSourceConstants.FACEBOOK;
    const isGorgias = msgSourceName === msgSourceConstants.GORGIAS;
    const isZendesk = msgSourceName === msgSourceConstants.ZENDESK;
    const supportsInternalNotes = isGorgias || isZendesk;

    const [fetchingBacklog, setFetchingBacklog] = useState(false);

    const persistIsActive = async (next) => {
        const response = await organizationSettingsService.updateTrendsMsgDataSource(
            trendsId,
            {isActive: next}
        );
        return _.get(response, 'data.isActive');
    };

    const persistMsgDataSourceField = (field) => async (next) => {
        const response = await organizationSettingsService.updateTrendsMsgDataSource(
            trendsId,
            {[field]: next}
        );
        return _.get(response, `data.${field}`);
    };

    const persistFacebookField = (field) => async (next) => {
        const response = await organizationSettingsService.updateTrendsFacebookMsgDataSource(
            trendsId,
            {[field]: next}
        );
        return _.get(response, `data.${field}`);
    };

    const persistGorgiasField = (field) => async (next) => {
        const response = await organizationSettingsService.updateTrendsGorgiasMsgDataSource(
            trendsId,
            {[field]: next}
        );
        return _.get(response, `data.${field}`);
    };

    const isActive = useToggle({
        initial: trendsMsgDataSource?.isActive,
        persist: persistIsActive,
        successMessage: (next) => next
            ? 'Msg data source activated.'
            : 'Msg data source deactivated.',
        fallbackMessage: 'Failed to update active status.',
    });

    const fetchAdsPostsEnabled = useToggle({
        initial: trendsMsgDataSource?.fetchAdsPostsEnabled,
        persist: persistFacebookField('fetchAdsPostsEnabled'),
        successMessage: (next) => next
            ? 'Fetching ad posts enabled.'
            : 'Fetching ad posts disabled.',
        fallbackMessage: 'Failed to update fetch ads posts setting.',
    });

    const adPollingEnabled = useToggle({
        initial: trendsMsgDataSource?.v2_AdPollingEnabled,
        persist: persistFacebookField('v2_AdPollingEnabled'),
        successMessage: (next) => next
            ? 'Ad polling (v2) enabled.'
            : 'Ad polling (v2) disabled.',
        fallbackMessage: 'Failed to update ad polling setting.',
    });

    const internalNotesEnabled = useToggle({
        initial: trendsMsgDataSource?.internalNotesEnabled,
        persist: persistMsgDataSourceField('internalNotesEnabled'),
        successMessage: (next) => next
            ? 'Internal notes enabled.'
            : 'Internal notes disabled.',
        fallbackMessage: 'Failed to update internal notes setting.',
    });

    const actionByAssignee = useToggle({
        initial: trendsMsgDataSource?.actionByAssignee,
        persist: persistGorgiasField('actionByAssignee'),
        successMessage: (next) => next
            ? 'Action by assignee enabled.'
            : 'Action by assignee disabled.',
        fallbackMessage: 'Failed to update action by assignee setting.',
    });

    const handleFetchBacklog = async () => {
        if (fetchingBacklog || trendsId == null) return;
        setFetchingBacklog(true);
        try {
            await organizationSettingsService.triggerTrendsZendeskFetchBacklog(trendsId);
            SnackBar.showMessage('Backlog fetch triggered.');
        } catch (error) {
            handleApiError(error, 'Failed to trigger backlog fetch.');
        } finally {
            setFetchingBacklog(false);
        }
    };

    if (trendsId == null) {
        return null;
    }

    return (
        <Box sx={styles.container}>
            <Box component={'h4'} sx={commonStyles.labelBold}>
                {label?.TRENDS}
            </Box>
            <Grid container columnSpacing={4} rowSpacing={1}>
                <Grid item xs={12} md={4}>
                    <ToggleField label={label?.ACTIVE} toggle={isActive} />
                </Grid>
                {supportsInternalNotes && (
                    <Grid item xs={12} md={4}>
                        <ToggleField label={label?.INTERNAL_NOTES} toggle={internalNotesEnabled} />
                    </Grid>
                )}
                {isGorgias && (
                    <Grid item xs={12} md={4}>
                        <ToggleField label={label?.ACTION_BY_ASSIGNEE} toggle={actionByAssignee} />
                    </Grid>
                )}
                {isGorgias && (
                    <Grid item xs={12} md={4}>
                        <GorgiasViewsSetupEntry
                            trendsId={trendsId}
                            label={label?.GORGIAS_VIEWS_SETUP}
                            buttonLabel={label?.OPEN}
                        />
                    </Grid>
                )}
                {isZendesk && (
                    <Grid item xs={12} md={4}>
                        <ActionField
                            label={label?.FETCH_BACKLOG}
                            buttonLabel={fetchingBacklog ? 'Fetching…' : label?.FETCH}
                            busy={fetchingBacklog}
                            onAction={handleFetchBacklog}
                        />
                    </Grid>
                )}
                {isFacebook && (
                    <>
                        <Grid item xs={12} md={4}>
                            <ToggleField label={label?.FETCH_AD_POSTS} toggle={fetchAdsPostsEnabled} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <ToggleField label={label?.AD_POLLING_V2} toggle={adPollingEnabled} />
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
}

const styles = {
    container: {
        mb: 2,
    },
    toggleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 0.5,
    },
    actionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 0.5,
    },
    actionButton: {
        width: '160px',
    },
    spinner: {
        color: '#003300',
    },
};
